import { useState } from "react";
import LeadForm from "@/components/forms/LeadForm";
import WaitlistForm from "@/components/forms/WaitlistForm";
import { BRAND } from "@/data/content";
import { Mail, Phone, MapPin, CalendarCheck, ArrowUpRight } from "lucide-react";
import { track } from "@/lib/analytics";

export default function ContactPage() {
  const [tab, setTab] = useState("waitlist");

  return (
    <div data-testid="contact-page" className="pt-32 sm:pt-40 pb-16 sm:pb-20">
      <section className="px-5 sm:px-6 lg:px-12 pb-10 sm:pb-12">
        <div className="max-w-[1400px] mx-auto">
          <p className="overline">Contact</p>
          <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[8rem] leading-[0.95] tracking-tight mt-5 sm:mt-6 max-w-[14ch]">
            Tell us your <span className="italic text-aurora">ambition.</span>
          </h1>
          <p className="text-base sm:text-lg text-neutral-500 max-w-2xl mt-6 sm:mt-10 leading-relaxed">
            Join the founding cohort, book a 30-minute strategy call, or just
            say hi. Every message is read by a human and answered within one
            business day.
          </p>
        </div>
      </section>

      <section className="px-5 sm:px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 sm:gap-12 lg:gap-16">
          <div className="lg:col-span-4 order-2 lg:order-1">
            <div className="border border-neutral-200 p-6 sm:p-8 lg:p-10">
              <p className="overline">Direct</p>
              <h3 className="font-serif text-2xl sm:text-3xl mt-3 sm:mt-4 mb-6 sm:mb-8">
                Skip the form. Reach out.
              </h3>
              <ul className="space-y-6">
                <li className="flex gap-4">
                  <Mail
                    className="text-gold mt-1 flex-none"
                    size={18}
                    strokeWidth={1.5}
                  />
                  <a
                    href={`mailto:${BRAND.email}`}
                    className="text-neutral-900 hover:text-gold transition break-all"
                    data-testid="contact-email"
                  >
                    {BRAND.email}
                  </a>
                </li>
                <li className="flex gap-4">
                  <Phone
                    className="text-gold mt-1 flex-none"
                    size={18}
                    strokeWidth={1.5}
                  />
                  <span className="text-neutral-900">{BRAND.phone}</span>
                </li>
                <li className="flex gap-4">
                  <MapPin
                    className="text-gold mt-1 flex-none"
                    size={18}
                    strokeWidth={1.5}
                  />
                  <span className="text-neutral-900">{BRAND.location}</span>
                </li>
              </ul>
            </div>

            <a
              href={BRAND.calendly}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-calendly"
              onClick={() => track("CTA Clicked", { location: "contact_sidebar", label: "Book a call", destination: "calendly" })}
              className="block mt-5 sm:mt-6 border border-gold/40 hover:border-gold p-6 sm:p-8 lg:p-10 bg-gold/[0.03] group"
            >
              <div className="flex items-center gap-3 mb-3">
                <CalendarCheck size={18} className="text-gold" />
                <p className="overline">Strategy call</p>
              </div>
              <p className="font-serif text-xl sm:text-2xl leading-snug">
                Book 30 minutes with the studio.
                <span className="italic text-aurora"> Free.</span>
              </p>
              <p className="text-neutral-500 text-sm mt-3 leading-relaxed">
                Bring a problem. Leave with a roadmap. No pitch deck, no sales dance.
              </p>
              <span className="inline-flex items-center gap-2 mt-5 text-xs uppercase tracking-[0.2em] text-gold group-hover:gap-3 transition-all">
                Open Calendly <ArrowUpRight size={14} />
              </span>
            </a>
          </div>

          <div className="lg:col-span-7 lg:col-start-6 order-1 lg:order-2">
            <div className="flex gap-1 mb-8 sm:mb-10 border-b border-neutral-200 overflow-x-auto">
              <button
                onClick={() => {
                  setTab("waitlist");
                  track("Contact Tab Switched", { tab: "waitlist" });
                }}
                data-testid="tab-waitlist"
                className={`px-4 sm:px-6 py-3 sm:py-4 text-[10px] sm:text-xs uppercase tracking-[0.2em] transition border-b-2 -mb-px whitespace-nowrap ${
                  tab === "waitlist"
                    ? "border-gold text-gold"
                    : "border-transparent text-neutral-500 hover:text-neutral-900"
                }`}
              >
                Join the waitlist
              </button>
              <button
                onClick={() => {
                  setTab("hello");
                  track("Contact Tab Switched", { tab: "hello" });
                }}
                data-testid="tab-hello"
                className={`px-4 sm:px-6 py-3 sm:py-4 text-[10px] sm:text-xs uppercase tracking-[0.2em] transition border-b-2 -mb-px whitespace-nowrap ${
                  tab === "hello"
                    ? "border-gold text-gold"
                    : "border-transparent text-neutral-500 hover:text-neutral-900"
                }`}
              >
                Just say hi
              </button>
            </div>

            {tab === "waitlist" ? (
              <WaitlistForm />
            ) : (
              <LeadForm source="contact_page" />
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
